package no.vareflow.app.importer

import no.vareflow.app.domain.*

/** What a spreadsheet column is used for. */
enum class ImportField(private val nb: String, private val en: String) {
    NAME("Varenavn", "Product name"),
    NUMBER("Varenummer", "Product number"),
    CATEGORY("Kategori / hovedvare", "Category / main category"),
    UNIT("Enhet", "Unit"),
    PRICE("Innkjøpspris eks. mva", "Purchase price excl. VAT"),
    VAT("MVA %", "VAT %"),
    SUPPLIER("Leverandør", "Supplier");

    val label: String get() = t(nb, en)
}

/** Where the hovedvare (main category) of each product comes from. */
enum class CategorySource(private val nb: String, private val en: String) {
    /** The category column (e.g. "Brennevin", "Vin"). */
    COLUMN("Kolonnen «Kategori»", "The «Category» column"),
    /** Heading rows between the products (e.g. "KJØTT", "FISK"). */
    HEADINGS("Overskriftsrader i arket", "Heading rows in the sheet"),
    /** One hovedvare for everything in the file. */
    SINGLE("Én hovedvare for alt", "One main category for all");

    val label: String get() = t(nb, en)
}

/** Existing products found again: update price/unit/VAT/supplier, or leave them as they are. */
enum class DuplicateMode { UPDATE, SKIP }

data class ImportSettings(
    val headerRow: Int,
    val columns: Map<ImportField, Int>,
    val categorySource: CategorySource,
    val singleCategory: String = "",
    val duplicates: DuplicateMode = DuplicateMode.UPDATE,
    /** Hovedvare renames chosen in the preview: name in the file (normalized) → name to use. */
    val categoryRenames: Map<String, String> = emptyMap(),
    /** Heading rows inside a hovedvare (e.g. RØDVIN, HVITVIN under Vin) become the products' undergruppe. */
    val useUndergruppe: Boolean = true
)

/** One product row as read from the sheet, before matching against existing products. */
data class ImportRow(
    val sheetRow: Int,
    val name: String,
    val varenummer: String,
    val category: String,
    val subgroup: String,
    val unit: String,
    val price: Double?,
    val vat: Double?,
    val supplier: String
)

data class SkippedRow(val sheetRow: Int, val text: String, val reason: String)

data class CategorySummary(
    val name: String,
    val count: Int,
    val isNew: Boolean,
    /** Undergrupper in this hovedvare with their product counts, in file order (empty: none). */
    val undergrupper: List<Pair<String, Int>> = emptyList()
)

data class ImportPlan(
    val settings: ImportSettings,
    val newProducts: List<Product>,
    /** (existing, updated) for products already in Vareflow. */
    val updates: List<Pair<Product, Product>>,
    val unchanged: List<Product>,
    val skipped: List<SkippedRow>,
    val categories: List<CategorySummary>,
    val headings: List<String>,
    val missingPrice: Int,
    val missingVat: Int
) {
    val total: Int get() = newProducts.size + updates.size + unchanged.size
}

/**
 * Product import from a spreadsheet (Excel or CSV). Finds the header row and the columns by their
 * names, reads every product row, takes the hovedvare from the category column or from heading
 * rows ("KJØTT", "FISK" …), cleans up spelling variants, and matches existing products by
 * varenummer + name (or name + hovedvare). New hovedvarer simply appear: a hovedvare is the
 * category of its products.
 */
object ProductImport {
    private val HEADER_WORDS: Map<ImportField, List<String>> = mapOf(
        ImportField.NAME to listOf("varenavn", "produktnavn", "artikkelnavn", "navn", "vare", "produkt", "beskrivelse", "artikkel", "product", "name", "description"),
        ImportField.NUMBER to listOf("varenummer", "varenumer", "varenr", "vnr", "artikkelnummer", "artikkelnr", "artnr", "art.nr", "epd", "gtin", "ean", "nummer", "nr", "item number", "sku"),
        ImportField.CATEGORY to listOf("hovedvare", "hovedgruppe", "varegruppe", "kategori", "gruppe", "category", "group"),
        ImportField.UNIT to listOf("enhet", "enh", "eining", "unit"),
        ImportField.PRICE to listOf("enhetspris", "innkjøpspris", "innkjopspris", "kostpris", "pris", "price", "cost"),
        ImportField.VAT to listOf("mva %", "mva%", "mva-sats", "mvasats", "mva", "moms", "vat"),
        ImportField.SUPPLIER to listOf("leverandør", "leverandor", "lev", "grossist", "supplier", "vendor")
    )
    /** Headers that look like a field but are totals or amounts, never the field itself. */
    private val NOT_FIELD = listOf("verdi", "beløp", "belop", "sum", "total", "kr", "antall", "kommentar", "dato", "inkl")
    private val TOTAL_WORDS = listOf("total", "totalt", "sum", "summer", "oppsummering", "sluttsum")

    fun normalizeHeader(s: String): String = s.trim().lowercase().replace(Regex("\\s+"), " ")

    /** Which field a header text means, or null. Longer keywords win ("varenavn" before "vare"). */
    fun fieldFor(header: String): ImportField? {
        val h = normalizeHeader(header)
        if (h.isEmpty()) return null
        var best: Pair<ImportField, Int>? = null
        for ((field, words) in HEADER_WORDS) {
            for (w in words) {
                val hit = h == w || h.startsWith("$w ") || h.startsWith("$w.") || h.startsWith("$w,") || (w.length >= 5 && h.contains(w))
                if (!hit) continue
                // "MVA kr", "Verdi eks. mva", "MVA-beløp", "Verdi inkl. mva" are amounts.
                val blocked = NOT_FIELD.any { bad -> Regex("(^|[^a-zæøå])${Regex.escape(bad)}([^a-zæøå]|$)").containsMatchIn(h) } &&
                    !(field == ImportField.PRICE && h.contains("pris"))
                if (blocked) continue
                if (best == null || w.length > best.second) best = field to w.length
            }
        }
        return best?.first
    }

    /** Header row (0-based) and the columns found in it: the row in the first 25 with the most known headers. */
    fun detectColumns(sheet: SheetData): Pair<Int, Map<ImportField, Int>>? {
        var best: Pair<Int, Map<ImportField, Int>>? = null
        for (r in 0 until minOf(sheet.rows.size, 25)) {
            val found = LinkedHashMap<ImportField, Int>()
            sheet.rows[r].forEachIndexed { c, cell ->
                if (cell.number != null) return@forEachIndexed
                val f = fieldFor(cell.text) ?: return@forEachIndexed
                if (f !in found) found[f] = c
            }
            if (ImportField.NAME !in found) continue
            if (found.size >= 2 && (best == null || found.size > best.second.size)) best = r to found
        }
        return best
    }

    /** Sheets that look like product lists, best first (most product rows). */
    fun rankSheets(sheets: List<SheetData>): List<Pair<SheetData, Int>> =
        sheets.map { sheet ->
            val detected = detectColumns(sheet)
            val count = if (detected == null) 0 else readRows(sheet, detected.first, detected.second).first.size
            sheet to count
        }.sortedByDescending { it.second }

    // ---------------------------------------------------------------- cleaning

    fun cleanText(s: String): String = s.replace(' ', ' ').trim().replace(Regex("\\s+"), " ")

    /** "KJØTT" → "Kjøtt", "frukt / grønt" → "Frukt/grønt"; mixed case is kept ("Frukt/Grønt"). */
    fun cleanCategory(raw: String): String {
        var s = cleanText(raw).replace(Regex("\\s*/\\s*"), "/").trimEnd('.', ':')
        if (s.isEmpty()) return s
        val letters = s.filter { it.isLetter() }
        if (letters.isNotEmpty() && letters.all { it.isUpperCase() }) s = s.lowercase()
        return s.replaceFirstChar { it.uppercase() }
    }

    /** Spelling key for matching: case, spaces and punctuation ignored. */
    fun key(s: String): String = s.lowercase().filter { it.isLetterOrDigit() }

    /** 4020101.0 → "4020101", " 9801 25 " → "9801 25". */
    fun cleanNumber(cell: SheetCell): String = cell.number?.let { XlsxReader.formatNumber(it) } ?: cleanText(cell.text)

    /** "pr.kg" → "kg", "KG" → "kg", "st" → "stk", "" → "stk". */
    fun cleanUnit(raw: String): String {
        var u = cleanText(raw).lowercase().removePrefix("pr.").removePrefix("pr ").trim()
        u = when (u) {
            "", "-" -> "stk"
            "st", "st.", "stk.", "styk", "stykk" -> "stk"
            "l", "ltr", "liter" -> "liter"
            "fl", "flk", "flaske" -> "flaske"
            "pak", "pakke", "pk." -> "pk"
            "po", "pose" -> "pos"
            "esk.", "eske" -> "esk"
            else -> u
        }
        return u
    }

    fun parsePrice(cell: SheetCell): Double? = cell.number ?: CsvReader.parseNumber(cell.text)

    /** 0.25 / 25 / "25 %" → 25.0; only 15 and 25 are used in Vareflow (others → nearest of the two). */
    fun parseVat(cell: SheetCell): Double? {
        val v = cell.number ?: CsvReader.parseNumber(cell.text) ?: return null
        val percent = if (v <= 1.0) v * 100.0 else v
        return if (percent >= 20.0) 25.0 else 15.0
    }

    private fun isTotalText(s: String): Boolean = key(s).let { k -> TOTAL_WORDS.any { k == it || k.startsWith(it) } }

    // ---------------------------------------------------------------- reading

    /** Product rows (with the raw category and heading) and skipped rows, in sheet order. */
    fun readRows(sheet: SheetData, headerRow: Int, columns: Map<ImportField, Int>): Pair<List<ImportRow>, List<SkippedRow>> {
        val rows = mutableListOf<ImportRow>()
        val skipped = mutableListOf<SkippedRow>()
        val nameCol = columns[ImportField.NAME] ?: return rows to skipped
        val used = columns.values.toSet()
        var heading = ""
        var lastCategory = ""
        for (r in headerRow + 1 until sheet.rows.size) {
            val row = sheet.rows[r]
            if (row.all { it.isBlank }) continue
            fun cell(f: ImportField) = columns[f]?.let { sheet.cell(r, it) } ?: SheetCell.EMPTY
            val name = cleanText(sheet.cell(r, nameCol).text.ifBlank { sheet.cell(r, nameCol).number?.let { XlsxReader.formatNumber(it) }.orEmpty() })
            val price = parsePrice(cell(ImportField.PRICE))
            val unit = cleanText(cell(ImportField.UNIT).text)
            val number = cleanNumber(cell(ImportField.NUMBER))
            val rawCategory = cleanText(cell(ImportField.CATEGORY).text)
            val texts = row.filter { !it.isBlank }
            val rowText = texts.joinToString(" · ") { it.text }
            // A heading: only text in the first columns, no price, no unit, nothing numeric.
            val isHeading = price == null && unit.isEmpty() && texts.isNotEmpty() && texts.size <= 2 &&
                texts.all { it.number == null && it.text.any(Char::isLetter) } &&
                row.indexOfFirst { !it.isBlank } <= maxOf(nameCol, used.minOrNull() ?: 0) + 1
            if (isHeading) {
                // "Frukt | Frukt/Grønt": the text in the name column is the fuller one.
                val label = sheet.cell(r, nameCol).text.takeIf { it.isNotBlank() } ?: texts.first().text
                if (isTotalText(label)) { skipped += SkippedRow(r + 1, rowText, t("Sumlinje", "Total line")); continue }
                heading = cleanCategory(label)
                continue
            }
            if (name.isEmpty()) {
                skipped += SkippedRow(r + 1, rowText, t("Mangler varenavn", "No product name"))
                continue
            }
            if (isTotalText(name) && price == null) {
                skipped += SkippedRow(r + 1, rowText, t("Sumlinje", "Total line"))
                continue
            }
            if (price == null && unit.isEmpty() && number.isEmpty() && rawCategory.isEmpty()) {
                skipped += SkippedRow(r + 1, rowText, t("Ser ikke ut som en vare (ingen pris, enhet eller varenummer)", "Does not look like a product (no price, unit or number)"))
                continue
            }
            // A product without a category gets the one of the product above it (as in a list sorted by group).
            val category = rawCategory.ifEmpty { lastCategory }
            if (rawCategory.isNotEmpty()) lastCategory = rawCategory
            rows += ImportRow(
                sheetRow = r + 1, name = name, varenummer = number, category = category, subgroup = heading,
                unit = unit, price = price, vat = parseVat(cell(ImportField.VAT)), supplier = cleanText(cell(ImportField.SUPPLIER).text)
            )
        }
        return rows to skipped
    }

    /** COLUMN when the category column has few, clean values; HEADINGS when the column is messy or missing. */
    fun suggestCategorySource(rows: List<ImportRow>, hasCategoryColumn: Boolean): CategorySource {
        val headings = rows.map { it.subgroup }.filter { it.isNotEmpty() }.map(::key).distinct()
        val columnValues = rows.map { it.category }.filter { it.isNotEmpty() }.map(::key).distinct()
        return when {
            !hasCategoryColumn || columnValues.isEmpty() -> if (headings.isNotEmpty()) CategorySource.HEADINGS else CategorySource.SINGLE
            headings.isEmpty() -> CategorySource.COLUMN
            columnValues.size <= maxOf(12, headings.size * 2) -> CategorySource.COLUMN
            else -> CategorySource.HEADINGS
        }
    }

    /** Default settings for a sheet, or null when no header row with a product name column is found. */
    fun suggestSettings(sheet: SheetData): ImportSettings? {
        val (headerRow, columns) = detectColumns(sheet) ?: return null
        val rows = readRows(sheet, headerRow, columns).first
        return ImportSettings(headerRow, columns, suggestCategorySource(rows, ImportField.CATEGORY in columns), singleCategory = sheet.name.trim())
    }

    /** Most common spelling per key (e.g. "ASKO" over "Asko" and "ASKO "). */
    private fun canonicalSpellings(values: List<String>): Map<String, String> =
        values.filter { it.isNotBlank() }.groupBy(::key).mapValues { (_, list) ->
            list.groupingBy { it }.eachCount().entries.sortedWith(compareByDescending<Map.Entry<String, Int>> { it.value }.thenBy { it.key }).first().key
        }

    // ---------------------------------------------------------------- plan

    fun plan(sheet: SheetData, settings: ImportSettings, existing: List<Product>, existingCategories: List<String>, fileName: String = ""): ImportPlan {
        val (rows, skippedRows) = readRows(sheet, settings.headerRow, settings.columns)
        val skipped = skippedRows.toMutableList()
        val existingCategoryByKey = existingCategories.associateBy(::key)

        val rawCategories = rows.map { row ->
            when (settings.categorySource) {
                CategorySource.COLUMN -> cleanCategory(row.category)
                CategorySource.HEADINGS -> row.subgroup.ifEmpty { cleanCategory(row.category) }
                CategorySource.SINGLE -> cleanCategory(settings.singleCategory)
            }
        }
        val spelling = canonicalSpellings(rawCategories)
        val supplierSpelling = canonicalSpellings(rows.map { it.supplier })
        fun categoryOf(raw: String): String {
            if (raw.isBlank()) return ""
            val k = key(raw)
            val renamed = settings.categoryRenames[k]?.let(::cleanText)?.takeIf { it.isNotEmpty() }
            val name = renamed ?: spelling[k] ?: raw
            return existingCategoryByKey[key(name)] ?: name
        }

        val byNumberAndName = existing.filter { it.varenummer.isNotBlank() }.associateBy { key(it.varenummer) + "|" + key(it.name) }
        val byNameAndCategory = existing.associateBy { key(it.name) + "|" + key(it.category) }
        val byName = existing.groupBy { key(it.name) }
        val seen = HashSet<String>()
        val newProducts = mutableListOf<Product>()
        val updates = mutableListOf<Pair<Product, Product>>()
        val unchanged = mutableListOf<Product>()
        var missingPrice = 0
        var missingVat = 0
        val source = fileName.ifBlank { sheet.name }

        // With the category column as hovedvare, a heading ("ØL PRODUKTER") is a subgroup only while
        // the category stays the same: it does not continue into the Mineralvann rows below it.
        val columnSubgroups = run {
            var current = ""
            var currentCategory: String? = null
            var ended = false
            rows.map { row ->
                if (row.subgroup != current) { current = row.subgroup; currentCategory = null; ended = false }
                val k = key(row.category)
                if (currentCategory == null) currentCategory = k else if (k != currentCategory) ended = true
                if (ended) "" else row.subgroup
            }
        }

        // Undergruppe: the heading rows inside a hovedvare (clean names). The messy category column of
        // a heading-based sheet is never used. A hovedvare whose products would all get the same
        // undergruppe (e.g. Øl under "ØL PRODUKTER") is not divided.
        val undergruppeCandidates = rows.indices.map { i ->
            if (!settings.useUndergruppe) "" else when (settings.categorySource) {
                CategorySource.COLUMN -> columnSubgroups[i]
                CategorySource.SINGLE -> rows[i].subgroup
                CategorySource.HEADINGS -> ""
            }.let(::cleanUndergruppe)
        }
        val divided: Set<String> = rows.indices.groupBy { key(categoryOf(rawCategories[it])) }
            .filterValues { idx -> idx.map { key(undergruppeCandidates[it]) }.distinct().size > 1 }
            .keys

        rows.forEachIndexed { i, row ->
            val category = categoryOf(rawCategories[i])
            val undergruppe = if (key(category) in divided && key(undergruppeCandidates[i]) != key(category)) undergruppeCandidates[i] else ""
            if (category.isEmpty()) {
                skipped += SkippedRow(row.sheetRow, row.name, t("Mangler hovedvare", "No main category"))
                return@forEachIndexed
            }
            val identity = key(row.varenummer) + "|" + key(row.name)
            if (!seen.add(identity)) {
                skipped += SkippedRow(row.sheetRow, row.name, t("Står to ganger i filen", "Listed twice in the file"))
                return@forEachIndexed
            }
            val subgroup = when (settings.categorySource) {
                CategorySource.COLUMN -> columnSubgroups[i]
                CategorySource.HEADINGS -> cleanCategory(row.category)
                CategorySource.SINGLE -> listOf(row.subgroup, cleanCategory(row.category)).firstOrNull { it.isNotEmpty() }.orEmpty()
            }.takeIf { key(it) != key(category) }.orEmpty()
            if (row.price == null) missingPrice++
            if (row.vat == null) missingVat++
            val notes = listOfNotNull(
                if (row.price == null) t("mangler pris", "no price") else null,
                if (row.vat == null) t("mangler MVA (satt til 15 %)", "no VAT (set to 15 %)") else null
            )
            val supplier = supplierSpelling[key(row.supplier)] ?: row.supplier
            val match = (if (row.varenummer.isNotBlank()) byNumberAndName[identity] else null)
                ?: byNameAndCategory[key(row.name) + "|" + key(category)]
                ?: byName[key(row.name)]?.singleOrNull()?.takeIf { row.varenummer.isBlank() || it.varenummer.isBlank() }
            if (match != null) {
                if (settings.duplicates == DuplicateMode.SKIP) { unchanged += match; return@forEachIndexed }
                val updated = match.copy(
                    varenummer = match.varenummer.ifBlank { row.varenummer },
                    unit = if (row.unit.isNotEmpty()) cleanUnit(row.unit) else match.unit,
                    pricePerUnit = row.price ?: match.pricePerUnit,
                    vat = row.vat ?: match.vat,
                    supplier = supplier.ifBlank { match.supplier },
                    sourceRow = row.sheetRow,
                    sourceCategory = subgroup.ifEmpty { match.sourceCategory },
                    undergruppe = undergruppe.ifEmpty { match.undergruppe }
                )
                if (updated == match.copy(sourceRow = row.sheetRow, sourceCategory = updated.sourceCategory)) unchanged += match else updates += match to updated
                return@forEachIndexed
            }
            newProducts += Product(
                varenummer = row.varenummer,
                name = row.name,
                unit = cleanUnit(row.unit),
                pricePerUnit = row.price ?: 0.0,
                category = category,
                vat = row.vat ?: 15.0,
                supplier = supplier,
                importStatus = if (notes.isEmpty()) "Importert" else "Kontroller",
                dataQuality = (listOf(t("Importert fra $source rad ${row.sheetRow}", "Imported from $source row ${row.sheetRow}")) + notes).joinToString(" · "),
                sourceRow = row.sheetRow,
                sourceCategory = subgroup,
                undergruppe = undergruppe
            )
        }

        val allProducts = newProducts + updates.map { it.second } + unchanged
        val all = allProducts.map { it.category }
        val categories = all.groupingBy { it }.eachCount().entries
            .sortedBy { e -> all.indexOf(e.key) }
            .map { (name, count) ->
                val inCategory = allProducts.filter { it.category == name && it.undergruppe.isNotBlank() }.map { it.undergruppe }
                CategorySummary(name, count, existingCategoryByKey[key(name)] == null,
                    inCategory.groupingBy { it }.eachCount().entries.sortedBy { e -> inCategory.indexOf(e.key) }.map { it.key to it.value })
            }
        return ImportPlan(
            settings = settings,
            newProducts = newProducts,
            updates = updates,
            unchanged = unchanged,
            skipped = skipped.sortedBy { it.sheetRow },
            categories = categories,
            headings = rows.map { it.subgroup }.filter { it.isNotEmpty() }.distinct(),
            missingPrice = missingPrice,
            missingVat = missingVat
        )
    }

    /** Reads a file by its name: .xlsx (Excel) or .csv/.txt. Old .xls files are not supported. */
    fun readFile(fileName: String, data: ByteArray): List<SheetData> {
        val lower = fileName.lowercase()
        return when {
            lower.endsWith(".xls") && !ZipReader.isZip(data) -> throw SpreadsheetException(
                t("Gamle .xls-filer støttes ikke. Åpne filen i Excel og lagre som .xlsx.", "Old .xls files are not supported. Open the file in Excel and save it as .xlsx."))
            ZipReader.isZip(data) -> XlsxReader.read(data)
            else -> listOf(CsvReader.read(data, fileName.substringBeforeLast('.')))
        }
    }
}
